import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Modal, Form, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import {
  FaSpinner, FaCheckCircle, FaTimesCircle, FaArrowLeft,
  FaShoppingCart, FaWhatsapp, FaUser, FaPhone,
  FaSearch, FaFilter, FaCheck,
  FaMoneyBillWave, FaUsers,
  FaSort, FaSortUp, FaSortDown
} from 'react-icons/fa';
import './admin-ecards.css';
import { useAuthContext } from '../../context/AuthContext';
import { invitationApi } from '../../api/guestList';
import StyledDataTable from '../../components/StyledDataTable/StyledDataTable';
import { guestApi } from '../../api/guestList';
import { paymentApi } from '../../api/payments';
import PaymentModal from '../../modals/payments/PaymentModal/PaymentModal';
import { eventApi } from '../../api/events';
import { ecardApi } from '../../api/eCards/ecardApi';
import { settingsApi } from '../../api/settings';

const AdminCardDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const { currentEvent, isAuthenticated, user } = useAuthContext();

  const { card: stateCard } = location.state || {};
  const [card, setCard] = useState(stateCard || null);
  const [cardLoading, setCardLoading] = useState(!stateCard);

  const imagePath = card?.photo_path || card?.lg_path || card?.wallpaper || card?.preview_path;
  const priceInt = card ? (parseInt(card.price) || 0) : 0;

  // Custom templates are assigned templates for events, general templates can be used by anyone
  // Preview templates require request customization on WhatsApp
  const isAssigned = card?.template_type === 'custom' || card?.template_type === 'general';
  const isPreview = card?.template_type === 'preview';

  // Fetch card details on mount
  useEffect(() => {
    const fetchCardDetails = async () => {
      setCardLoading(true);
      try {
        const response = await ecardApi.getTemplateDetails(id);
        const cardDetails = response?.data?.data || response?.data || response || {};
        if (cardDetails && typeof cardDetails === 'object' && Object.keys(cardDetails).length > 0) {
          setCard(cardDetails);
        }
      } catch (err) {
        console.error('Error fetching card details:', err);
        if (!stateCard) {
          setError('Failed to load card template details. Please try again.');
        }
      } finally {
        setCardLoading(false);
      }
    };

    if (id) {
      fetchCardDetails();
    }
  }, [id, stateCard]);

  // Pagination state
  const [perPage, setPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const features = [
    { name: "Dynamic Details & Card Number", included: true },
    { name: "Sending card link via SMS", included: true },
    { name: "Sending card via Email", included: true },
    { name: "Sent List Report (PDF)", included: true },
    { name: "Sending card via WhatsApp", included: priceInt >= 750 },
    { name: "QR Code", included: priceInt >= 750 },
    { name: "Custom Template", included: priceInt >= 750 },
    { name: "RSVP Link", included: priceInt >= 750 },
    { name: "Bride/Groom Photo", included: priceInt >= 950 },
    { name: "Auto Thank you Message", included: priceInt >= 950 },
  ];

  // State for data
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating] = useState(false);

  // UI State
  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState('NOT_GENERATED');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showCardPreviewModal, setShowCardPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generatedPreviewUrl, setGeneratedPreviewUrl] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [dynamicMnoOptions, setDynamicMnoOptions] = useState([]);
  const [currentPaymentId, setCurrentPaymentId] = useState(null);
  const [initiateLoading, setInitiateLoading] = useState(false);

  // General Template States
  const [invitationIntro, setInvitationIntro] = useState('');
  const [invitationInfo, setInvitationInfo] = useState('');

  // Fetch custom templates if it is a general template
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await settingsApi.getCustomTemplates();
        const settings = response?.data?.data || response?.data || response || {};
        setInvitationIntro(settings.invitation_intro || '');
        setInvitationInfo(settings.invitation_info || '');
      } catch (err) {
        console.error('Failed to fetch custom message templates:', err);
        setInvitationIntro('');
        setInvitationInfo('');
      }
    };

    if (isAuthenticated && card?.template_type === 'general') {
      fetchSettings();
    }
  }, [isAuthenticated, card?.template_type]);

  // Hide Customer Support widget on this page
  useEffect(() => {
    document.body.classList.add('hide-customer-support');
    return () => {
      document.body.classList.remove('hide-customer-support');
    };
  }, []);

  // Fetch guests on component mount
  useEffect(() => {
    if (isAuthenticated && currentEvent) {
      fetchGuests();
    }
  }, [isAuthenticated, currentEvent]);

  const fetchGuests = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await invitationApi.getInvitationList();
      let guestsList = [];

      if (Array.isArray(response)) {
        guestsList = response;
      } else if (Array.isArray(response?.guests)) {
        guestsList = response.guests;
      } else if (Array.isArray(response?.data)) {
        guestsList = response.data;
      } else if (Array.isArray(response?.data?.guests)) {
        guestsList = response.data.guests;
      }

      if (guestsList.length === 0) {
        const fallback = await guestApi.getGuests();
        if (Array.isArray(fallback)) {
          guestsList = fallback;
        } else if (Array.isArray(fallback?.data)) {
          guestsList = fallback.data;
        } else if (Array.isArray(fallback?.guests)) {
          guestsList = fallback.guests;
        }
      }

      const formattedGuests = guestsList.map(guest => {
        const invCard = guest.invitation_card || {};
        // card_type: 1 = Single, 2 = Double
        const cardType = Number(invCard.card_type) || Number(guest.card_type) || 1;
        return {
          id: guest.id,
          name: guest.name || guest.full_name,
          phone: guest.phone_number || guest.mobile,
          pledge_amount: parseFloat(guest.pledge) || 0,
          paid_amount: parseFloat(guest.total_fund) || 0,
          card_type: cardType === 2 ? 'Double' : 'Single',
          card_type_value: cardType,
          card_number: invCard.card_number || guest.card_number || null,
          table_number: invCard.table_number || guest.table_number || null,
          email: guest.email,
          generated_status: (Number(guest.generated_status) === 1 || Number(invCard.generated_status) === 1) ? 'GENERATED' : 'NOT_GENERATED',
          sent_status: (guest.card_sent === true || Number(guest.sent_status) === 1 || Number(invCard.sent_status) === 1) ? 'SENT' : 'NOT_SENT'
        };
      });

      setGuests(formattedGuests);
    } catch (err) {
      console.error('Error fetching guests:', err);
      setGuests([]);
      if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else if (err.response?.status === 403) {
        toast.error('You do not have permission to view this page.');
      } else if (err.response?.status !== 404) {
        setError('Failed to load guests. Please refresh the page.');
        toast.error('Failed to load guests');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle card type change
  const handleTypeChange = (guestId, newType) => {
    setGuests(prevData =>
      prevData.map(guest =>
        guest.id === guestId ? { ...guest, card_type: newType } : guest
      )
    );
  };

  const handleGenerateCards = () => {
    if (selectedContacts.length === 0) {
      toast.warning('Please select at least one contact');
      return;
    }
    setShowReviewModal(true);
  };

  const handleOpenCardPreview = async () => {
    setShowReviewModal(false);
    setShowCardPreviewModal(true);
    setPreviewLoading(true);
    setGeneratedPreviewUrl(null);

    try {
      const templateId = card?.id || id;
      const payload = {
        invitation_intro: invitationIntro,
        invitation_info: invitationInfo
      };

      const response = await ecardApi.generatePreview(templateId, payload);

      const previewUrl =
        (typeof response === 'string' && (response.startsWith('http') || response.startsWith('data:image'))) ? response :
          response?.data?.preview_path ||
          response?.preview_path ||
          response?.data?.data?.preview_path ||
          response?.data?.preview_url ||
          response?.preview_url ||
          response?.data?.url ||
          response?.url ||
          response?.data?.image ||
          response?.image;

      if (previewUrl) {
        setGeneratedPreviewUrl(previewUrl);
      } else {
        setGeneratedPreviewUrl(imagePath);
      }
    } catch (err) {
      console.error('Error generating card preview:', err);
      toast.info('Haungeweza kupakia mchoro wa kadi ya mfano. Inatumia picha ya sasa.');
      setGeneratedPreviewUrl(imagePath);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleProceedFromReview = async () => {
    setInitiateLoading(true);
    try {
      const selectedGuestData = guests.filter(g => selectedContacts.includes(g.id));

      const paymentData = {
        service: "ecard",
        amount: calculateTotalPrice(),
        phone_number: user?.mobile || user?.phone_number || user?.phone || "0000000000",
        customer_name: currentEvent?.client_name || user?.name || 'Admin',
        template_id: card?.id || id,
        guests: selectedGuestData.map(g => ({
          guest_id: g.id,
          card_type: g.card_type === 'Double' ? '2' : '1',
          card_number: g.card_number || 'A00'
        })),
        invitation_intro: card?.template_type === 'general' ? invitationIntro : '',
        invitation_info: card?.template_type === 'general' ? invitationInfo : ''
      };

      const initiateResponse = await paymentApi.initiatePayment(paymentData);
      const paymentPayload = initiateResponse?.data || initiateResponse;

      if (initiateResponse?.success || initiateResponse?.status === 800 || initiateResponse?.status === 201 || paymentPayload?.payment) {
        const paymentId = paymentPayload?.payment?.id || paymentPayload?.id;
        const options = paymentPayload?.mno_options || [];

        if (!paymentId) throw new Error('Could not retrieve payment ID from initiation response.');

        setCurrentPaymentId(paymentId);
        setDynamicMnoOptions(options);

        setShowReviewModal(false);
        setShowPaymentModal(true);
      } else {
        handleApiResponse(initiateResponse);
      }
    } catch (err) {
      console.error('Error initiating payment:', err);
      handleApiResponse(err);
    } finally {
      setInitiateLoading(false);
    }
  };

  // Click anywhere on row to select/deselect guest
  const toggleContactSelection = (guestId) => {
    setSelectedContacts(prev =>
      prev.includes(guestId)
        ? prev.filter(id => id !== guestId)
        : [...prev, guestId]
    );
  };

  const selectAllFiltered = () => {
    const allFilteredIds = filteredItems.map(item => item.id);
    if (selectedContacts.length === allFilteredIds.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(allFilteredIds);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === 'desc') {
        setSortDirection('asc');
      } else {
        setSortField(null);
        setSortDirection('desc');
      }
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <FaSort className="ms-1 text-muted opacity-50" style={{ fontSize: '0.75rem' }} />;
    }
    return sortDirection === 'asc' ? (
      <FaSortUp className="ms-1 text-primary" style={{ fontSize: '0.85rem' }} />
    ) : (
      <FaSortDown className="ms-1 text-primary" style={{ fontSize: '0.85rem' }} />
    );
  };

  const filteredItems = useMemo(() => {
    let result = guests.filter(item => {
      // 1. Status Filter
      if (statusFilter !== 'ALL' && item.generated_status !== statusFilter) {
        return false;
      }

      // 2. Text Search Filter (name or phone)
      if (filterText.trim()) {
        const lowerSearch = filterText.toLowerCase();
        const matchName = item.name?.toLowerCase().includes(lowerSearch);
        const matchPhone = item.phone?.toLowerCase().includes(lowerSearch);
        if (!matchName && !matchPhone) {
          return false;
        }
      }

      return true;
    });

    if (sortField) {
      result = [...result].sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];

        if (typeof valA === 'string') {
          valA = valA.toLowerCase();
          valB = (valB || '').toLowerCase();
          return sortDirection === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        }

        valA = Number(valA) || 0;
        valB = Number(valB) || 0;
        return sortDirection === 'asc' ? valA - valB : valB - valA;
      });
    }

    return result;
  }, [guests, statusFilter, filterText, sortField, sortDirection]);

  const calculateTotalPrice = () => {
    const pricePerCard = card?.price ? parseFloat(card.price.replace(/[^0-9.]/g, '')) : 750;
    return (pricePerCard * selectedContacts.length).toFixed(2);
  };

  const handleCloseModal = () => setShowModal(false);
  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
  };

  // Status filter options
  const statusOptions = [
    { value: 'NOT_GENERATED', label: 'Not Generated' },
    { value: 'GENERATED', label: 'Generated' },
    { value: 'ALL', label: 'All' },
  ];

  // Statistics
  const totalGuests = guests.length;
  const generatedCount = guests.filter(g => g.generated_status === 'GENERATED').length;
  const pendingCount = totalGuests - generatedCount;
  const selectedCount = selectedContacts.length;

  if (loading || cardLoading) {
    return (
      <div className="admin-card-details-loading">
        <div className="loading-spinner-container">
          <FaSpinner className="fa-spin" size={48} color="#4169e1" />
          <p className="mt-3 text-muted">Loading card details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="admin-main-content py-5">
        <Alert variant="danger" className="text-center">
          <Alert.Heading>Error Loading Data</Alert.Heading>
          <p>{error}</p>
          <Button onClick={() => fetchGuests()} variant="outline-danger">Retry</Button>
        </Alert>
      </Container>
    );
  }

  if (!card) {
    return (
      <Container className="admin-main-content py-5">
        <Alert variant="warning" className="text-center">
          <Alert.Heading>Card Not Found</Alert.Heading>
          <p>The requested card template could not be found.</p>
          <Button onClick={() => window.history.back()} variant="outline-warning">Go Back</Button>
        </Alert>
      </Container>
    );
  }

  return (
    <div className="admin-card-details-modern">
      <br></br>
      <Container fluid className="px-3 px-md-4 py-3">
        {/* Back Button */}
        <Button variant="link" className="back-button" onClick={() => window.history.back()}>
          <FaArrowLeft className="me-2" /> Back to Cards
        </Button>

        {/* Card Preview Section - Modern Layout */}
        <Row className="g-4 mb-5">
          <Col lg={5} xl={4}>
            <div className="card-preview-container">
              <div className="card-image-wrapper" onClick={() => setShowModal(true)}>
                <img
                  src={imagePath}
                  alt={card.name}
                  className="card-preview-image"
                />
                <div className="card-image-overlay">
                  <span className="click-to-enlarge">🔍 Click to enlarge</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={7} xl={8}>
            <div className="card-details-panel">
              <div className="card-header-section">
                <h2 className="card-title">{card.name}</h2>
                <div className="card-tags">
                  <span className="tag tag-primary">{card.card_category}</span>
                  <span className="tag tag-secondary">{card.event_type}</span>
                </div>
                <div className="card-price">
                  <span className="price-amount">TZS {priceInt.toLocaleString()}</span>
                  <span className="price-label">per card</span>
                </div>
              </div>

              <div className="card-features-section">
                <h5 className="features-title">✨ Features Included</h5>
                <div className="features-grid">
                  {features.map((feature, idx) => (
                    <div key={idx} className={`feature-item ${feature.included ? 'included' : 'excluded'}`}>
                      {feature.included ? (
                        <FaCheckCircle className="feature-icon included" />
                      ) : (
                        <FaTimesCircle className="feature-icon excluded" />
                      )}
                      <span className="feature-name">{feature.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {card?.template_type === 'general' && (
                <div className="general-template-form mt-4 p-3 rounded border" style={{ backgroundColor: '#f8fafc', borderColor: '#e2e8f0' }}>
                  <h6 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: '#24366b' }}>
                    Kadi ya Mwaliko (Invitation Text)
                  </h6>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small text-secondary">Utangulizi wa Mwaliko (Intro)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={invitationIntro}
                      onChange={(e) => setInvitationIntro(e.target.value)}
                      placeholder="Weka utangulizi..."
                      style={{ fontSize: '0.85rem', borderRadius: '8px' }}
                    />
                  </Form.Group>
                  <Form.Group className="mb-0">
                    <Form.Label className="fw-semibold small text-secondary">Taarifa za Mwaliko (Info)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={invitationInfo}
                      onChange={(e) => setInvitationInfo(e.target.value)}
                      placeholder="Weka taarifa za shughuli..."
                      style={{ fontSize: '0.85rem', borderRadius: '8px' }}
                    />
                  </Form.Group>
                </div>
              )}

              {(card?.template_type === 'preview' || (card?.template_type !== 'general' && card?.template_type !== 'custom' && isPreview)) && (
                <div className="customization-section mt-4">
                  <a
                    href={`https://wa.me/255747636516?text=I%20need%20customization%20on%20this%20card:%20${encodeURIComponent(card.name)}%20(Price:%20TZS%20${priceInt.toLocaleString()}).`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whatsapp-customize-btn"
                  >
                    <FaWhatsapp className="me-2" /> Request Card Customization
                  </a>
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Guest List Executive Container */}
        {isAssigned ? (
          <div>
            <div className="bg-white p-3 p-md-4 rounded-4 shadow-sm border mb-4">
              {/* Top Row: Title, Stats & Action Button */}
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
                <div className="d-flex flex-wrap align-items-center gap-2 gap-md-3">
                  <h4 className="fw-bold mb-0 d-flex align-items-center gap-2 text-dark" style={{ fontSize: '1.25rem' }}>
                    <FaUsers style={{ color: '#4f46e5' }} /> Guest List
                  </h4>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="badge rounded-pill bg-light text-secondary border px-3 py-2 fw-semibold" style={{ fontSize: '0.78rem' }}>
                      Total: {totalGuests}
                    </span>
                    <span className="badge rounded-pill px-3 py-2 fw-semibold" style={{ backgroundColor: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', fontSize: '0.78rem' }}>
                      Generated: {generatedCount}
                    </span>
                    <span className="badge rounded-pill px-3 py-2 fw-semibold" style={{ backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', fontSize: '0.78rem' }}>
                      Pending: {pendingCount}
                    </span>
                    <span className="badge rounded-pill px-3 py-2 fw-semibold" style={{ backgroundColor: '#e0e7ff', color: '#4338ca', border: '1px solid #c7d2fe', fontSize: '0.78rem' }}>
                      Selected: {selectedCount}
                    </span>
                  </div>
                </div>

                <Button
                  variant="primary"
                  disabled={selectedContacts.length === 0 || generating}
                  onClick={handleGenerateCards}
                  className="rounded-3 px-4 py-2 fw-semibold shadow-sm border-0 d-flex align-items-center gap-2 text-nowrap"
                  style={{
                    background: selectedContacts.length > 0 ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : '#a5b4fc',
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    boxShadow: selectedContacts.length > 0 ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none'
                  }}
                >
                  <FaShoppingCart /> Generate Cards ({selectedContacts.length})
                </Button>
              </div>

              {/* Bottom Row: Search & Filter Toolbar */}
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-3">
                {/* Search Bar */}
                <div className="position-relative flex-grow-1" style={{ maxWidth: '360px' }}>
                  <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" style={{ zIndex: 5, fontSize: '0.85rem' }} />
                  <Form.Control
                    type="text"
                    placeholder="Search name or phone..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    className="ps-5 shadow-none border"
                    style={{ borderRadius: '25px', height: '40px', fontSize: '0.875rem', borderColor: '#cbd5e1' }}
                  />
                </div>

                {/* Status Filter Pills */}
                <div className="bg-light p-1 rounded-pill border d-inline-flex align-items-center gap-1">
                  <FaFilter className="text-muted ms-2 me-1" style={{ fontSize: '0.8rem' }} />
                  {statusOptions.map(status => (
                    <button
                      key={status.value}
                      type="button"
                      onClick={() => setStatusFilter(status.value)}
                      className="btn btn-sm rounded-pill px-3 py-1 fw-semibold transition-all border-0"
                      style={{
                        backgroundColor: statusFilter === status.value ? '#3b82f6' : 'transparent',
                        color: statusFilter === status.value ? '#ffffff' : '#64748b',
                        fontSize: '0.825rem'
                      }}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="table-responsive-modern d-none d-md-block">
              <table className="guest-table">
                <thead>
                  <tr>
                    <th className="col-number">#</th>
                    <th className="col-select">
                      <Form.Check
                        type="checkbox"
                        checked={selectedContacts.length > 0 && selectedContacts.length === filteredItems.length}
                        onChange={selectAllFiltered}
                      />
                    </th>
                    <th className="col-name" onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      <span className="d-inline-flex align-items-center">
                        Name {renderSortIcon('name')}
                      </span>
                    </th>
                    <th className="col-phone" onClick={() => handleSort('phone')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      <span className="d-inline-flex align-items-center">
                        Phone {renderSortIcon('phone')}
                      </span>
                    </th>
                    <th className="col-amount" style={{ userSelect: 'none' }}>
                      <span className="d-inline-flex align-items-center gap-1">
                        <span
                          role="button"
                          onClick={() => handleSort('pledge_amount')}
                          title="Panga kwa Ahadi (Sort by Pledge)"
                          className={`cursor-pointer ${sortField === 'pledge_amount' ? 'text-primary fw-bold' : ''}`}
                          style={{ cursor: 'pointer' }}
                        >
                          Pledge {renderSortIcon('pledge_amount')}
                        </span>
                        <span className="text-muted mx-1">/</span>
                        <span
                          role="button"
                          onClick={() => handleSort('paid_amount')}
                          title="Panga kwa Mchango (Sort by Paid)"
                          className={`cursor-pointer ${sortField === 'paid_amount' ? 'text-primary fw-bold' : ''}`}
                          style={{ cursor: 'pointer' }}
                        >
                          Paid {renderSortIcon('paid_amount')}
                        </span>
                      </span>
                    </th>
                    <th className="col-type">Type</th>
                    <th className="col-status">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length > 0 ? (
                    filteredItems.slice((currentPage - 1) * perPage, currentPage * perPage).map((item, index) => {
                      // Calculate global index across pages
                      const globalIndex = (currentPage - 1) * perPage + index + 1;
                      const isSelected = selectedContacts.includes(item.id);
                      return (
                        <tr
                          key={item.id}
                          className={`${isSelected ? 'selected' : ''}`}
                          onClick={() => toggleContactSelection(item.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="col-number">{globalIndex}</td>
                          <td className="col-select" onClick={(e) => e.stopPropagation()}>
                            <Form.Check
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleContactSelection(item.id)}
                            />
                          </td>
                          <td className="col-name">
                            <div className="guest-name-cell">
                              <div className="avatar">
                                <FaUser size={14} />
                              </div>
                              <span>{item.name}</span>
                            </div>
                          </td>
                          <td className="col-phone">
                            <FaPhone size={12} className="phone-icon" />
                            {item.phone}
                          </td>
                          <td className="col-amount">
                            <span className="pledge-amount">TZS {item.pledge_amount.toLocaleString()}</span>
                            <span className="separator">/</span>
                            <span className="paid-amount">TZS {item.paid_amount.toLocaleString()}</span>
                          </td>
                          <td className="col-type" onClick={(e) => e.stopPropagation()}>
                            <div className="type-selector">
                              <button
                                onClick={() => handleTypeChange(item.id, 'Single')}
                                className={`type-btn ${item.card_type === 'Single' ? 'active-single' : ''}`}
                              >
                                Single
                              </button>
                              <button
                                onClick={() => handleTypeChange(item.id, 'Double')}
                                className={`type-btn ${item.card_type === 'Double' ? 'active-double' : ''}`}
                              >
                                Double
                              </button>
                            </div>
                          </td>
                          <td className="col-status">
                            <span className={`status-badge ${item.generated_status === 'GENERATED' ? 'generated' : 'pending'}`}>
                              {item.generated_status === 'GENERATED' ? 'Generated' : 'NOT GENERATED'}
                            </span>
                            {item.generated_status === "GENERATED" && (
                              <span className={`status-badge ${item.sent_status === 'SENT' ? 'sent' : 'not-sent'}`}>
                                {item.sent_status === 'SENT' ? 'Sent' : 'Not Sent'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4 text-muted">
                        No guests found matching your criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {filteredItems.length > 0 && (
                <div className="table-pagination">
                  <div className="pagination-info">
                    <span>
                      Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, filteredItems.length)} of {filteredItems.length} entries
                    </span>
                    <div className="pagination-controls">
                      <select
                        value={perPage}
                        onChange={(e) => {
                          setPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="per-page-select"
                      >

                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={300}>300</option>
                        <option value={500}>500</option>
                      </select>
                      <div className="page-buttons">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="page-btn"
                        >
                          Previous
                        </button>
                        <span className="page-current">
                          Page {currentPage} of {Math.ceil(filteredItems.length / perPage)}
                        </span>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredItems.length / perPage)))}
                          disabled={currentPage === Math.ceil(filteredItems.length / perPage)}
                          className="page-btn"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile View: Dedicated responsive card-based list */}
            <div className="d-block d-md-none">
              {/* Mobile Toolbar: Select All & Quick Sort */}
              <div className="d-flex flex-column gap-2 mb-3 p-3 bg-white rounded-3 border shadow-sm">
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Check
                    type="checkbox"
                    id="select-all-mobile-ecards"
                    label={<span className="fw-semibold text-dark" style={{ fontSize: '0.85rem' }}>Chagua Wote ({selectedContacts.length}/{filteredItems.length})</span>}
                    checked={selectedContacts.length > 0 && selectedContacts.length === filteredItems.length}
                    onChange={selectAllFiltered}
                  />
                  <span className="badge bg-light text-secondary border rounded-pill px-2 py-1" style={{ fontSize: '0.75rem' }}>
                    Uk. {currentPage}/{Math.max(1, Math.ceil(filteredItems.length / perPage))}
                  </span>
                </div>

                {/* Mobile Quick Sort Pills */}
                <div className="d-flex align-items-center gap-1 flex-wrap pt-2 border-top">
                  <span className="text-muted small me-1" style={{ fontSize: '0.75rem' }}>Panga:</span>
                  <button
                    type="button"
                    onClick={() => handleSort('pledge_amount')}
                    className={`btn btn-sm rounded-pill px-2 py-0 fw-semibold border ${sortField === 'pledge_amount' ? 'btn-primary text-white' : 'btn-light text-secondary'}`}
                    style={{ fontSize: '0.75rem' }}
                  >
                    Ahadi {renderSortIcon('pledge_amount')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSort('paid_amount')}
                    className={`btn btn-sm rounded-pill px-2 py-0 fw-semibold border ${sortField === 'paid_amount' ? 'btn-primary text-white' : 'btn-light text-secondary'}`}
                    style={{ fontSize: '0.75rem' }}
                  >
                    Mchango {renderSortIcon('paid_amount')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSort('name')}
                    className={`btn btn-sm rounded-pill px-2 py-0 fw-semibold border ${sortField === 'name' ? 'btn-primary text-white' : 'btn-light text-secondary'}`}
                    style={{ fontSize: '0.75rem' }}
                  >
                    Jina {renderSortIcon('name')}
                  </button>
                </div>
              </div>

              {/* Mobile Guest Cards List */}
              <div className="mobile-guest-cards d-flex flex-column gap-3 mb-4">
                {filteredItems.length > 0 ? (
                  filteredItems.slice((currentPage - 1) * perPage, currentPage * perPage).map((item, index) => {
                    const globalIndex = (currentPage - 1) * perPage + index + 1;
                    const isSelected = selectedContacts.includes(item.id);
                    return (
                      <div
                        key={item.id}
                        className={`p-3 bg-white rounded-3 border shadow-sm position-relative transition-all ${isSelected ? 'border-primary' : ''}`}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#f8faff' : '#ffffff',
                          borderWidth: isSelected ? '2px' : '1px'
                        }}
                        onClick={() => toggleContactSelection(item.id)}
                      >
                        {/* Card Header: Index, Checkbox & Badges */}
                        <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                          <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Form.Check
                              type="checkbox"
                              id={`mobile-check-${item.id}`}
                              checked={isSelected}
                              onChange={() => toggleContactSelection(item.id)}
                              style={{ transform: 'scale(1.15)', cursor: 'pointer' }}
                            />
                            <span className="text-muted fw-bold" style={{ fontSize: '0.8rem' }}>#{globalIndex}</span>
                          </div>
                          <div className="d-flex align-items-center gap-1 flex-wrap justify-content-end">
                            <span className={`status-badge ${item.generated_status === 'GENERATED' ? 'generated' : 'pending'}`} style={{ fontSize: '0.7rem', padding: '2px 8px' }}>
                              {item.generated_status === 'GENERATED' ? 'Generated' : 'NOT GENERATED'}
                            </span>
                            {item.generated_status === "GENERATED" && (
                              <span className={`status-badge ${item.sent_status === 'SENT' ? 'sent' : 'not-sent'}`} style={{ fontSize: '0.7rem', padding: '2px 8px' }}>
                                {item.sent_status === 'SENT' ? 'Sent' : 'Not Sent'}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Guest Details: Avatar, Name & Phone */}
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: '36px', height: '36px', backgroundColor: '#e0e7ff', color: '#4338ca' }}
                          >
                            <FaUser size={14} />
                          </div>
                          <div className="overflow-hidden">
                            <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.95rem' }}>{item.name}</div>
                            <div className="text-muted d-flex align-items-center gap-1" style={{ fontSize: '0.8rem' }}>
                              <FaPhone size={10} />
                              <span>{item.phone}</span>
                            </div>
                          </div>
                        </div>

                        {/* Bottom Row: Pledge/Paid & Type Selector */}
                        <div className="d-flex justify-content-between align-items-center pt-2 border-top flex-wrap gap-2">
                          <div>
                            <div className="text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ahadi / Mchango</div>
                            <div style={{ fontSize: '0.85rem' }}>
                              <span className="fw-semibold text-secondary">TZS {item.pledge_amount.toLocaleString()}</span>
                              <span className="text-muted mx-1">/</span>
                              <span className="fw-bold" style={{ color: '#10b981' }}>TZS {item.paid_amount.toLocaleString()}</span>
                            </div>
                          </div>

                          <div className="type-selector" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleTypeChange(item.id, 'Single')}
                              className={`type-btn ${item.card_type === 'Single' ? 'active-single' : ''}`}
                              style={{ padding: '3px 10px', fontSize: '0.75rem' }}
                            >
                              Single
                            </button>
                            <button
                              onClick={() => handleTypeChange(item.id, 'Double')}
                              className={`type-btn ${item.card_type === 'Double' ? 'active-double' : ''}`}
                              style={{ padding: '3px 10px', fontSize: '0.75rem' }}
                            >
                              Double
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-4 text-center bg-white rounded-3 border text-muted">
                    Hakuna wageni waliopatikana.
                  </div>
                )}
              </div>

              {/* Mobile Pagination */}
              {filteredItems.length > 0 && (
                <div className="d-flex flex-column align-items-center justify-content-center gap-2 p-3 bg-white rounded-3 border shadow-sm mb-4">
                  <span className="text-muted small">
                    Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, filteredItems.length)} of {filteredItems.length} entries
                  </span>
                  <div className="d-flex align-items-center gap-2 flex-wrap justify-content-center">
                    <select
                      value={perPage}
                      onChange={(e) => {
                        setPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="form-select form-select-sm"
                      style={{ width: 'auto', fontSize: '0.82rem' }}
                    >
                      <option value={10}>10 / uk</option>
                      <option value={25}>25 / uk</option>
                      <option value={50}>50 / uk</option>
                      <option value={100}>100 / uk</option>
                      <option value={300}>300 / uk</option>
                      <option value={500}>500 / uk</option>
                    </select>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="btn btn-outline-secondary btn-sm px-3"
                      style={{ fontSize: '0.82rem' }}
                    >
                      ‹ Nyuma
                    </button>
                    <span className="fw-semibold px-1" style={{ fontSize: '0.85rem' }}>
                      {currentPage} / {Math.ceil(filteredItems.length / perPage)}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredItems.length / perPage)))}
                      disabled={currentPage === Math.ceil(filteredItems.length / perPage)}
                      className="btn btn-outline-secondary btn-sm px-3"
                      style={{ fontSize: '0.82rem' }}
                    >
                      Mbele ›
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <FaWhatsapp className="empty-icon" />
            <h4 className="empty-title">Card Not Assigned</h4>
            <p className="empty-description">
              This card is not yet assigned to an event. Please use the request button above to contact the admin for customization.
            </p>
          </div>
        )}

        {/* Image Preview Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
          <Modal.Body className="p-0">
            <img src={imagePath} alt="Card Preview" style={{ width: '100%', height: 'auto' }} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
          </Modal.Footer>
        </Modal>

        {/* Floating Generate Cards Button */}
        {!(showModal || showReviewModal || showCardPreviewModal || showPaymentModal) && (
          <button
            className="floating-generate-btn"
            onClick={handleGenerateCards}
            title="Generate Selected Cards"
            disabled={selectedContacts.length === 0}
            style={{ opacity: selectedContacts.length === 0 ? 0.5 : 1, cursor: selectedContacts.length === 0 ? 'not-allowed' : 'pointer' }}
          >
            <span>Generate Cards</span>
            <span className="badge-count">{selectedContacts.length}</span>
          </button>
        )}
        {/* Review Modal */}
        <Modal show={showReviewModal} onHide={() => setShowReviewModal(false)} size="lg" centered>
          <Modal.Header closeButton className="review-header">
            <Modal.Title>
              Review Selected Guests ({selectedContacts.length})
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="review-body">
            <p className="review-subtitle">
              Review the guests below. You can still change the card type in the main table. Once ready, preview how the card will look.
            </p>
            <div className="review-table-wrapper">
              <table className="review-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Card Type</th>
                  </tr>
                </thead>
                <tbody>
                  {guests
                    .filter(g => selectedContacts.includes(g.id))
                    .map((guest, idx) => (
                      <tr key={guest.id}>
                        <td className="text-center">{idx + 1}</td>
                        <td><strong>{guest.name}</strong></td>
                        <td>{guest.phone}</td>
                        <td>
                          <span className={`review-type-badge ${guest.card_type === 'Double' ? 'double' : 'single'}`}>
                            {guest.card_type}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="review-summary">
              <span>
                <FaUsers className="me-2" />
                <strong>Total Guests:</strong> {selectedContacts.length}
              </span>
              <span>
                <FaMoneyBillWave className="me-2" />
                <strong>Total Price:</strong> TZS {calculateTotalPrice()}
              </span>
            </div>
          </Modal.Body>
          <Modal.Footer className="review-footer">
            <Button variant="outline-secondary" onClick={() => setShowReviewModal(false)} disabled={initiateLoading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleOpenCardPreview} disabled={initiateLoading}>
              Preview Card →
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Card Sample Preview Modal (Before Payment) */}
        <Modal show={showCardPreviewModal} onHide={() => setShowCardPreviewModal(false)} size="lg" centered>
          <Modal.Header closeButton className="bg-light border-0">
            <Modal.Title className="fw-bold d-flex align-items-center gap-2" style={{ color: '#1e293b', fontSize: '1.15rem' }}>
              Card Preview (Hakiki Kadi)
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4 text-center">
            <p className="text-muted small mb-3">
              Hivi ndivyo kadi yako itakavyoandaliwa na kutumwa kwa wageni. Hakiki muonekano kabla ya kuendelea na malipo.
            </p>

            <div className="preview-image-container p-3 rounded bg-light border d-flex justify-content-center align-items-center" style={{ minHeight: '320px' }}>
              {previewLoading ? (
                <div className="py-5 text-center">
                  <FaSpinner className="fa-spin text-primary mb-3" size={40} />
                  <p className="text-muted fw-semibold mb-0">Inaandaa kadi ya mfano... (Generating preview...)</p>
                </div>
              ) : (
                <img
                  key={generatedPreviewUrl || 'default-preview'}
                  src={generatedPreviewUrl || imagePath}
                  alt="Sample Card Preview"
                  className="img-fluid rounded shadow-sm"
                  style={{ maxHeight: '480px', objectFit: 'contain' }}
                />
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button
              variant="outline-secondary"
              onClick={() => {
                setShowCardPreviewModal(false);
                setShowReviewModal(true);
              }}
              disabled={initiateLoading}
            >
              ← Back to Review
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowCardPreviewModal(false);
                handleProceedFromReview();
              }}
              disabled={initiateLoading || previewLoading}
              className="px-4 fw-bold"
            >
              {initiateLoading ? 'Initiating Payment...' : 'Proceed to Payment →'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Payment Modal */}
        <PaymentModal
          show={showPaymentModal}
          handleClose={handleClosePaymentModal}
          amount={calculateTotalPrice()}
          title="Payment Details"
          paymentId={currentPaymentId}
          onPaymentSuccess={() => {
            toast.success("Malipo yamekamilika! Kadi zako zimeandaliwa kikamilifu.");
            handleClosePaymentModal();
            setSelectedContacts([]);
            navigate('/Generated-WhatsApp-Card');
          }}
          onPaymentError={(err) => {
            toast.error(err.message || "Failed to process payment");
          }}
          mnoOptions={dynamicMnoOptions}
          summaryChildren={
            <div className="payment-summary">
              <div className="payment-summary-item">
                <FaUsers className="me-2 text-primary" />
                <span>Selected Contacts</span>
                <strong>{selectedContacts.length}</strong>
              </div>
            </div>
          }
        />
      </Container>
    </div>
  );
};

export default AdminCardDetails;